'use client';

import posthog from 'posthog-js';
import { identifyPerson, setGroup } from '@/util/posthog/identify';
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { ServiceWithPricing, ServWithMods } from '@/util/types/serverTypes';
import {
  Service,
  ContactRoleType,
  ContactType,
  RequiredInfo,
  RequiredInfoValue,
  EquipmentQuantity,
  Bundle,
  Event,
  Inspector,
  Company,
} from '@/util/types/serverTypes';
import {
  getCompanyButtonStyles,
  getCompanyButtonFallbackClass,
} from '@/util/functions/ui/getCompanyButtonStyles';
import { AddressForScheduler, FoundationType } from '@/util/types/globalTypes';
import { BridgeParcel } from '@/util/functions/fetching/callZillowAndMatch';
import { OptimalSlotType } from '@/util/types/optimalSlotType';
import { usePriceCalculation } from '@/util/functions/schedulingHelpers';
import { pickExpandedAreaFee } from '@/util/functions/schedulingHelpers/pickExpandedAreaFee';
import { manageWorkingHold } from '@/util/functions/quote/manageWorkingHold';
import { saveDraftToStorage, clearDraftFromStorage } from './utils/draftStorage';
import { SCHEDULER_CONTACT_COOKIE } from '@/util/scheduling/schedulerRun';
import { ScheduleSettingsType } from '@/util/types/serverTypeCollection/scheduleSettings';
import { ServiceFilterType } from '@/util/types/serverTypeCollection/serviceFilter';
import { callServerAction } from '@/util/functions/fetching/callServerAction';
import { Quote, QuoteFullyPopulated } from '@/util/types/serverTypes';
import { createChargeObjects } from '@/util/functions/quote/createChargeObjects';
import { DiscountCode } from '@/util/types/serverTypeCollection/discounts';
import dayjs, { Dayjs } from 'dayjs';
import { EscapeHatchReason } from './utils/checkEscapeHatch';
import {
  discountRemovedNotice,
  EMPTY_SCHEDULER_PREFILL,
  getDiscountUnlockingServices,
  SchedulerPrefill,
  SchedulerPrefillNotice,
} from './utils/schedulerPrefill';
import { getEligibleServiceIds } from '@/util/functions/discountCodeValidator';

export type ContactFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

export type AdditionalContactSelection = {
  _roleId: string;
  _contactId: ContactType;
  primary: boolean;
};

export type AIRecommendation = {
  serviceId: string;
  serviceName: string;
  confidence: number;
  reason: string;
};

export type AIBundleRecommendation = {
  bundleId: string;
  bundleName: string;
  confidence: number;
  reason: string;
};

export type AIRecommendationsData = {
  globalReason: string;
  recommendations: AIRecommendation[];
  bundleRecommendations: AIBundleRecommendation[];
};

export type PropertyDetails = {
  bedrooms?: number;
  bedroomsVerified?: boolean;
  bathrooms?: number;
  bathroomsVerified?: boolean;
  hvacType?: string;
  hvacTypeVerified?: boolean;
  acType?: string;
  acTypeVerified?: boolean;
  foundationType?: FoundationType;
  foundationTypeVerified?: boolean;
  waterType?: 'city' | 'well';
  waterTypeVerified?: boolean;
  sewerType?: 'city' | 'septic';
  sewerTypeVerified?: boolean;
  poolCount: number;
  poolCountVerified?: boolean;
  garageCount: number;
  garageCountVerified?: boolean;
  outbuildingCount: number;
  outbuildingCountVerified?: boolean;
};

/**
 * Pages of the Property step the customer has to be shown before the step will
 * release them. `addons` is the AI upsell, which not every service configures —
 * see `seenPropertyPages` on the context type.
 */
export type PropertyPage = 'addons' | 'moreInfo';

export type SeenPropertyPages = Record<PropertyPage, boolean>;

const NO_PROPERTY_PAGES_SEEN: SeenPropertyPages = {
  addons: false,
  moreInfo: false,
};

type SchedulerContextType = {
  jwtToken: string;
  companyId: string;
  company: Company;
  serviceFilter: ServiceFilterType;
  scheduleSettings: ScheduleSettingsType;
  inspectors: Inspector[];
  services: ServWithMods[];
  companyStyles: {
    primaryButtonStyles: { backgroundColor?: string; color?: string };
    secondaryButtonStyles: { backgroundColor?: string; color?: string };
    primaryButtonClassName: string;
    secondaryButtonClassName: string;
    primaryButtonClassNameWithVars: string;
    secondaryButtonClassNameWithVars: string;
    cssVars: React.CSSProperties;
    companyColor: string | undefined;
    colorSecondary: string | undefined;
  };
  selectedPrimaryService: string | null;
  setSelectedPrimaryService: (serviceId: string | null) => void;
  selectedBundle: string | null;
  setSelectedBundle: (bundle: string | null) => void;
  selectedAddons: string[];
  setSelectedAddons: (addons: string[] | ((prev: string[]) => string[])) => void;
  selectedServiceIds: string[];
  selectedRoleIds: string[];
  setSelectedRoleIds: (roleIds: string[]) => void;
  selectedRole: ContactRoleType | null;
  setSelectedRole: (role: ContactRoleType | null) => void;
  contact: ContactType | null;
  setContact: (contact: ContactType | null) => void;
  additionalContacts: AdditionalContactSelection[];
  setAdditionalContacts: (
    contacts:
      | AdditionalContactSelection[]
      | ((prev: AdditionalContactSelection[]) => AdditionalContactSelection[])
  ) => void;
  address: AddressForScheduler | null;
  setAddress: (
    address:
      | AddressForScheduler
      | null
      | ((prev: AddressForScheduler | null) => AddressForScheduler | null)
  ) => void;
  streetViewUrl: string | null;
  setStreetViewUrl: (url: string | null) => void;
  zillowPropertyData: BridgeParcel | null;
  setZillowPropertyData: (data: BridgeParcel | null) => void;
  containingServiceAreaArray: OptimalSlotType['containingServiceArea'][number][] | null;
  setContainingServiceAreaArray: (
    areas: OptimalSlotType['containingServiceArea'][number][] | null
  ) => void;
  propertyDetails: PropertyDetails | null;
  setPropertyDetails: (details: PropertyDetails | null) => void;
  aiRecommendations: AIRecommendationsData | null;
  setAiRecommendations: (data: AIRecommendationsData | null) => void;
  isLoadingAiRecommendations: boolean;
  setIsLoadingAiRecommendations: (loading: boolean) => void;
  selectedDateTime: string | null;
  setSelectedDateTime: (datetime: string | null) => void;
  selectedInspectorId: string | null;
  setSelectedInspectorId: (inspectorId: string | null) => void;
  selectedInspectorForSlot: Record<string, string>;
  setSelectedInspectorForSlot: (mapping: Record<string, string>) => void;
  selectedInspectorIdsForFilter: string[];
  setSelectedInspectorIdsForFilter: (ids: string[]) => void;
  selectedSlotInspectorInfo: {
    _id: string;
    firstName: string;
    lastName: string;
    thumb: string;
  } | null;
  setSelectedSlotInspectorInfo: (
    info: {
      _id: string;
      firstName: string;
      lastName: string;
      thumb: string;
    } | null
  ) => void;
  /**
   * The company's full required-info catalog. Lives in context (not just as a
   * prop on the More Information step) so the step validators that gate
   * navigation can tell which questions are mandatory.
   */
  allRequiredInfo: RequiredInfo[];
  requiredInfoValues: RequiredInfoValue[];
  setRequiredInfoValues: (values: RequiredInfoValue[]) => void;
  equipmentQuantities: EquipmentQuantity[];
  setEquipmentQuantities: (quantities: EquipmentQuantity[]) => void;
  referralSource: string;
  setReferralSource: (source: string) => void;
  /** `YYYY-MM-DD`, or '' — optional, and never blocks a booking. */
  closingDate: string;
  setClosingDate: (value: string) => void;
  // Pricing & Calculated Services
  finalServices: ServiceWithPricing[];
  setFinalServices: (services: ServiceWithPricing[]) => void;
  priceData: {
    totalPrice: number;
    totalDuration: number;
    expandedAreaPrice: number;
  } | null;
  // Bundle and Discount Data
  bundles: Bundle[];
  discountCodes: DiscountCode[];
  appliedDiscountCode: DiscountCode | null;
  appliedDiscountServiceIds: string[];
  setAppliedDiscountCode: (code: DiscountCode | null) => void;
  setAppliedDiscountServiceIds: (serviceIds: string[]) => void;
  /**
   * A live discount code that does not apply to the cart yet — carried by a
   * scheduling link whose qualifying service the customer has not added, or set
   * aside when a cart change dropped an applied code.
   *
   * Never priced. It becomes `appliedDiscountCode` on its own the moment a
   * covered service enters the cart, so a booker who adds the advertised
   * service gets the advertised price without hunting for the discount box on
   * the final step.
   */
  pendingDiscountCode: DiscountCode | null;
  setPendingDiscountCode: (code: DiscountCode | null) => void;
  /**
   * What the customer could add to switch `pendingDiscountCode` on. Empty
   * whenever nothing is pending. The top-of-funnel banner and the AI upsell
   * page both read this, so they cannot disagree about which services matter.
   */
  pendingDiscountUnlockServices: ServWithMods[];
  // Lead Capture - Draft Quote State
  draftQuoteId: string | null;
  setDraftQuoteId: (id: string | null) => void;
  draftQuoteSlug: string | null;
  setDraftQuoteSlug: (slug: string | null) => void;
  wcLeadId: string | null;
  setWcLeadId: (id: string | null) => void;
  isLoadingDraft: boolean;
  setIsLoadingDraft: (loading: boolean) => void;
  shouldResumeDraft: boolean;
  setShouldResumeDraft: (should: boolean) => void;
  isCheckingDrafts: boolean;
  setIsCheckingDrafts: (checking: boolean) => void;
  isCreatingNewQuote: boolean;
  setIsCreatingNewQuote: (creating: boolean) => void;
  startNewQuote: () => void;
  // Working Hold State
  workingHold: Event | null;
  setWorkingHold: (hold: Event | null) => void;
  workingHoldExpiresAt: Dayjs | null;
  setWorkingHoldExpiresAt: (expiresAt: Dayjs | null) => void;
  // Booking State
  bookingStatus: 'idle' | 'loading' | 'success' | 'error';
  setBookingStatus: (status: 'idle' | 'loading' | 'success' | 'error') => void;
  // Sending Quote State
  isSendingQuote: boolean;
  setIsSendingQuote: (sending: boolean) => void;
  // Escape Hatch State
  escapeHatchReason: EscapeHatchReason;
  setEscapeHatchReason: (reason: EscapeHatchReason) => void;
  /**
   * Which of the Property step's internal pages the customer has been shown.
   *
   * The Property step is a stack of pages — Find Property → Review Details →
   * Recommended Services (only when the primary service has AI upsell addons
   * configured) → More Information — but the header's Next button advances the
   * *main* step. Data completeness alone therefore let a customer leave from
   * Review Details straight to Schedule, never seeing the upsell. It only showed
   * for companies whose services ask no mandatory questions: an unanswered
   * question happened to hold the gate shut everywhere else.
   *
   * Progress cannot be read back out of the data — a company may ask nothing at
   * all on More Information — so each page reports itself as it mounts, here,
   * where the header and the step guard can both see it. `addons` is reported by
   * `PropertyStep` instead when that page is not part of the flow, so consumers
   * never have to know whether it exists.
   *
   * Session-local by design, not saved to the draft: a returning customer is
   * placed past the Property step by `useAutoNavigateToStep`, and the gate only
   * applies while they are standing on that step.
   */
  seenPropertyPages: SeenPropertyPages;
  markPropertyPageSeen: (page: PropertyPage) => void;
  /** True once every page of the Property step has had its turn. */
  hasSeenPropertyPages: boolean;
  resetPropertyStepProgress: () => void;
  // Scheduler Notes (online booking instructions for the office)
  schedulerNotes: string;
  setSchedulerNotes: (notes: string) => void;
  // Draft save orchestrator
  cancelDebounce: () => void;
  // Internal - for DraftAutoSaver to set cancelDebounce ref
  setCancelDebounceRef: (fn: (() => void) | null) => void;
  contactRoles: ContactRoleType[];
  // WhatConverts tracking
  wcProfileId: string | null;
  // Scheduling-link pre-fill
  /** Sanitized cart/discount carried by the scheduling link. Never mutated. */
  prefill: SchedulerPrefill;
  /**
   * Customer-facing notices about the link — anything it asked for that could
   * not be honored, plus discounts dropped later because the cart changed.
   */
  prefillNotices: SchedulerPrefillNotice[];
  dismissPrefillNotices: () => void;
};

const SchedulerContext = createContext<SchedulerContextType | undefined>(undefined);

export const SchedulerProvider = ({
  children,
  services,
  bundles,
  discountCodes,
  scheduleSettings,
  serviceFilter,
  inspectors,
  company,
  jwtToken,
  companyId,
  contactRoles,
  allRequiredInfo = [],
  wcProfileId = null,
  quote = undefined,
  prefill = EMPTY_SCHEDULER_PREFILL,
}: {
  children: ReactNode;
  services: ServWithMods[];
  bundles: Bundle[];
  discountCodes: DiscountCode[];
  scheduleSettings: ScheduleSettingsType;
  serviceFilter: ServiceFilterType;
  inspectors: Inspector[];
  company: Company;
  jwtToken: string;
  companyId: string;
  contactRoles: ContactRoleType[];
  /**
   * The company's required-info catalog. Optional so the client quote portal and
   * the internal reschedule panel — which reuse this provider but do not run the
   * step-gated online funnel — can omit it. An empty catalog means nothing is
   * required, which is the correct reading for those callers.
   */
  allRequiredInfo?: RequiredInfo[];
  wcProfileId?: string | null;
  /**
   * Existing quote to price against (used by the client quote portal). When set,
   * `applyPriceOverrides` preserves the quote's charge amounts (and charge ids) for
   * unchanged services instead of repricing them at current rates; newly added
   * services still get freshly calculated prices. The online scheduler omits this.
   */
  quote?: QuoteFullyPopulated;
  /**
   * Cart + discount pre-fill resolved from the scheduling link's query params.
   * Seeds initial state only — the customer can change everything
   * afterwards, and a resumed draft deliberately overwrites the cart because it
   * represents the customer's own progress. Surfaces (like the client quote
   * portal) that reuse this provider simply omit it.
   */
  prefill?: SchedulerPrefill;
}) => {
  // Compute company styles for branding
  const companyStyles = useMemo(() => {
    const primaryStyles = getCompanyButtonStyles(company, false);
    const secondaryStyles = getCompanyButtonStyles(company, true);

    return {
      primaryButtonStyles: primaryStyles,
      secondaryButtonStyles: secondaryStyles,
      primaryButtonClassName: getCompanyButtonFallbackClass(company),
      secondaryButtonClassName: getCompanyButtonFallbackClass(company),
      // For components that need CSS variables in classNames (like Progress)
      primaryButtonClassNameWithVars: company?.color
        ? 'bg-[var(--company-primary)] text-[var(--company-primary-text)]'
        : 'bg-black text-white',
      secondaryButtonClassNameWithVars: company?.colorSecondary
        ? 'bg-[var(--company-secondary)] text-[var(--company-secondary-text)]'
        : 'bg-black text-white',
      // CSS custom properties to be applied to a parent element
      cssVars: {
        '--company-primary': company?.color || '#000000',
        '--company-primary-text': primaryStyles.color || '#ffffff',
        '--company-secondary': company?.colorSecondary || '#000000',
        '--company-secondary-text': secondaryStyles.color || '#ffffff',
      } as React.CSSProperties,
      companyColor: company?.color,
      colorSecondary: company?.colorSecondary,
    };
  }, [company]);

  // Service Related States — seeded from the scheduling link when it carried a
  // pre-filled cart. `prefill` is already validated against this company's
  // catalog, so these ids are always selectable.
  const [selectedPrimaryService, setSelectedPrimaryService] = useState<string | null>(
    () => prefill.primaryServiceId
  );
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>(() =>
    prefill.roleId ? [prefill.roleId] : []
  );
  const [selectedRole, setSelectedRole] = useState<ContactRoleType | null>(
    () => contactRoles.find((role) => role._id === prefill.roleId) ?? null
  );
  const [selectedBundle, setSelectedBundle] = useState<string | null>(() => prefill.bundleId);
  const [selectedAddons, setSelectedAddons] = useState<string[]>(
    () => prefill.addonServiceIds
  );

  // Compute selectedServiceIds from selection states
  const selectedServiceIds = useMemo(() => {
    const ids: string[] = [];

    // Add primary service
    if (selectedPrimaryService) {
      ids.push(selectedPrimaryService);
    }

    // Add addon services
    ids.push(...selectedAddons);

    // Add services from bundle
    if (selectedBundle) {
      const bundle = bundles.find((b) => b._id === selectedBundle);
      bundle?.items?.forEach((item) => {
        if (item._serviceId) {
          ids.push(item._serviceId);
        }
      });
    }

    // Deduplicate service IDs
    return [...new Set(ids)];
  }, [selectedPrimaryService, selectedAddons, selectedBundle, bundles]);

  // Contact Related States
  const [contact, setContact] = useState<ContactType | null>(null);
  const [additionalContacts, setAdditionalContacts] = useState<AdditionalContactSelection[]>([]);

  // Property/Address Related States
  const [address, setAddress] = useState<AddressForScheduler | null>(null);
  const [streetViewUrl, setStreetViewUrl] = useState<string | null>(null);
  const [zillowPropertyData, setZillowPropertyData] = useState<BridgeParcel | null>(null);
  const [containingServiceAreaArray, setContainingServiceAreaArray] = useState<
    OptimalSlotType['containingServiceArea'][number][] | null
  >(null);
  const [propertyDetails, setPropertyDetails] = useState<PropertyDetails | null>(null);

  // AI Recommendations
  const [aiRecommendations, setAiRecommendations] = useState<AIRecommendationsData | null>(null);
  const [isLoadingAiRecommendations, setIsLoadingAiRecommendations] = useState<boolean>(false);

  // Slot Selection States
  const [selectedDateTime, setSelectedDateTime] = useState<string | null>(null);

  // Inspector selection state - currently unused as backend automatically assigns best match
  // Kept for potential future feature: allow users to request specific inspectors
  const [selectedInspectorId, setSelectedInspectorId] = useState<string | null>(null);

  // Mapping of datetime -> inspectorId for multi-inspector slot selections
  const [selectedInspectorForSlot, setSelectedInspectorForSlot] = useState<Record<string, string>>(
    {}
  );

  // Inspector filter state for calendar view - when a single inspector is selected,
  // this triggers the requested inspector fee
  const [selectedInspectorIdsForFilter, setSelectedInspectorIdsForFilter] = useState<string[]>([]);

  // Store inspector info for selected slot
  const [selectedSlotInspectorInfo, setSelectedSlotInspectorInfo] = useState<{
    _id: string;
    firstName: string;
    lastName: string;
    thumb: string;
  } | null>(null);

  // Required Info and Equipment States
  const [requiredInfoValues, setRequiredInfoValues] = useState<RequiredInfoValue[]>([]);
  const [equipmentQuantities, setEquipmentQuantities] = useState<EquipmentQuantity[]>([]);
  const [referralSource, setReferralSource] = useState<string>('');
  const [closingDate, setClosingDate] = useState<string>('');

  // Lead Capture - Draft Quote States
  const [draftQuoteId, setDraftQuoteId] = useState<string | null>(null);
  const [draftQuoteSlug, setDraftQuoteSlug] = useState<string | null>(null);
  const [wcLeadId, setWcLeadId] = useState<string | null>(null);
  const [isLoadingDraft, setIsLoadingDraft] = useState<boolean>(false);
  const [shouldResumeDraft, setShouldResumeDraft] = useState<boolean>(false);
  const [isCheckingDrafts, setIsCheckingDrafts] = useState<boolean>(false);
  const [isCreatingNewQuote, setIsCreatingNewQuote] = useState<boolean>(false);

  // Working Hold State
  const [workingHold, setWorkingHold] = useState<Event | null>(null);
  const [workingHoldExpiresAt, setWorkingHoldExpiresAt] = useState<Dayjs | null>(null);

  // Booking State
  const [bookingStatus, setBookingStatus] = useState<'idle' | 'loading' | 'success' | 'error'>(
    'idle'
  );
  const [isSendingQuote, setIsSendingQuote] = useState<boolean>(false);

  // Escape Hatch State
  const [escapeHatchReason, setEscapeHatchReason] = useState<EscapeHatchReason>(null);

  // Property step progress — see the field's docblock on SchedulerContextType.
  const [seenPropertyPages, setSeenPropertyPages] =
    useState<SeenPropertyPages>(NO_PROPERTY_PAGES_SEEN);
  const markPropertyPageSeen = useCallback(
    (page: PropertyPage) =>
      setSeenPropertyPages((seen) =>
        seen[page] ? seen : { ...seen, [page]: true }
      ),
    []
  );
  const resetPropertyStepProgress = useCallback(
    () => setSeenPropertyPages(NO_PROPERTY_PAGES_SEEN),
    []
  );
  const hasSeenPropertyPages =
    seenPropertyPages.addons && seenPropertyPages.moreInfo;

  // Scheduler Notes
  const [schedulerNotes, setSchedulerNotes] = useState<string>('');

  // Discount Code State — pre-applied when the scheduling link carried a valid
  // promo, so the price the campaign advertised is the price shown from step 1.
  const [appliedDiscountCode, setAppliedDiscountCode] = useState<DiscountCode | null>(
    () => prefill.discountCode
  );
  const [appliedDiscountServiceIds, setAppliedDiscountServiceIds] = useState<string[]>(
    () => prefill.discountServiceIds
  );

  // A code the link carried that nothing in the cart qualifies for yet. Held so
  // adding the qualifying service can apply it — see the effect below.
  //
  // Session-local, like the notices: a customer who returns via the same link
  // has it resolved again server-side, and one who resumes a draft never had a
  // link-applied code in the first place.
  const [pendingDiscountCode, setPendingDiscountCode] = useState<DiscountCode | null>(
    () => prefill.pendingDiscountCode
  );

  // Pre-fill notices: what the link asked for but could not be honored, plus
  // discounts dropped later because the cart changed.
  const [prefillNotices, setPrefillNotices] = useState<SchedulerPrefillNotice[]>(
    () => prefill.notices
  );

  const dismissPrefillNotices = useCallback(() => setPrefillNotices([]), []);

  const pendingDiscountUnlockServices = useMemo(
    () =>
      pendingDiscountCode
        ? getDiscountUnlockingServices(
            pendingDiscountCode,
            services,
            selectedServiceIds
          )
        : [],
    [pendingDiscountCode, services, selectedServiceIds]
  );

  // Persist contact ID to localStorage whenever draft slug changes
  useEffect(() => {
    if (draftQuoteSlug && contact?._id) {
      // Save contact ID to localStorage
      saveDraftToStorage(contact._id);
    }
    // Note: We don't clear on null because user might be navigating away temporarily
    // Clearing happens explicitly when draft is completed or abandoned
  }, [draftQuoteSlug, contact?._id]);

  // Public online scheduler: no Attik auth. Identity = CRM contact after email step.
  // Browser context covers client-side telemetry. Cookie lets Express resolve
  // x-attik-scheduler-contact-id for backend observability.
  useEffect(() => {
    const rawId = contact?._id;
    const contactId =
      rawId != null && String(rawId).trim() !== '' ? String(rawId) : '';

    const secure =
      typeof window !== 'undefined' && window.location.protocol === 'https:';
    const cookieBase = `path=/; max-age=86400; SameSite=Lax${secure ? '; Secure' : ''}`;

    if (contactId) {
      if (typeof document !== 'undefined') {
        document.cookie = `${SCHEDULER_CONTACT_COOKIE}=${encodeURIComponent(contactId)}; ${cookieBase}`;
      }
    } else {
      if (typeof document !== 'undefined') {
        document.cookie = `${SCHEDULER_CONTACT_COOKIE}=; path=/; max-age=0`;
      }
    }

    // Keep PostHog person and company context aligned with the scheduler contact.
    posthog.register({ surface: 'web', company_id: String(companyId) });
    setGroup('company', String(companyId), { name: company.name ?? undefined });
    if (contactId) {
      identifyPerson(contactId, {
        name:
          [contact?.firstName, contact?.lastName].filter(Boolean).join(' ') ||
          undefined,
      });
      posthog.register({ attik_user_id: contactId, scheduler_contact_id: contactId });
    } else {
      posthog.unregister('attik_user_id');
      posthog.unregister('scheduler_contact_id');
    }
  }, [companyId, company.name, contact]);

  // Pricing & Calculated Services States
  const [finalServices, setFinalServices] = useState<ServiceWithPricing[]>([]);
  const [priceData, setPriceData] = useState<{
    totalPrice: number;
    totalDuration: number;
    expandedAreaPrice: number;
  } | null>(null);

  // Use ref to track previous services for price overrides
  const previousServicesRef = useRef<ServiceWithPricing[]>([]);

  // Every service area whose polygon contains the property — used for service-area
  // modifier conditions (e.g. inspector × area travel fees), independent of expandedFee.
  const serviceAreaIds = useMemo(
    () =>
      (containingServiceAreaArray ?? [])
        .map((area) => area?.serviceArea?._id)
        .filter((id): id is string => Boolean(id)),
    [containingServiceAreaArray]
  );

  // Use the new price calculation hook
  const {
    services: calculatedServices,
    totalPrice,
    totalDuration,
    expandedAreaPrice,
  } = usePriceCalculation({
    services,
    selectedServiceIds,
    address,
    serviceAreaCost: pickExpandedAreaFee(containingServiceAreaArray),
    requestedInspector: selectedInspectorIdsForFilter?.length === 1, // Trigger fee when filtering to single inspector
    requiredInfoValues,
    equipmentQuantities,
    inspectorIds: selectedSlotInspectorInfo?._id ? [selectedSlotInspectorInfo._id] : [],
    serviceAreaIds,
    bundle: selectedBundle ? bundles.find((b) => b._id === selectedBundle) || null : null,
    discountCode: appliedDiscountCode,
    appliedDiscountServiceIds,
    previousServices:
      previousServicesRef.current.length > 0 ? previousServicesRef.current : undefined,
    quote, // Set only by the client quote portal — preserves quoted charge amounts/ids
    onEquipmentChange: setEquipmentQuantities,
  });

  // Update finalServices and store in ref for next calculation
  useEffect(() => {
    // Only update if services actually changed (by comparing service IDs and prices)
    const hasChanged =
      calculatedServices.length !== finalServices.length ||
      calculatedServices.some((service, index) => {
        const prevService = finalServices[index];
        if (!prevService || service._id !== prevService._id) return true;
        if (service.totalPrice !== prevService.totalPrice) return true;
        if (service.priceOverride !== prevService.priceOverride) return true;
        const prevHasError = !!(prevService.modifierCalc && 'error' in prevService.modifierCalc);
        const currHasError = !!(service.modifierCalc && 'error' in service.modifierCalc);
        if (prevHasError !== currHasError) return true;
        return false;
      });

    if (hasChanged) {
      setFinalServices(calculatedServices);
      previousServicesRef.current = calculatedServices;
    }
  }, [calculatedServices, finalServices]);

  // Update priceData when totals change
  useEffect(() => {
    setPriceData((prev) => {
      // Only update if values actually changed
      if (
        prev?.totalPrice === totalPrice &&
        prev?.totalDuration === totalDuration &&
        prev?.expandedAreaPrice === expandedAreaPrice
      ) {
        return prev;
      }
      return { totalPrice, totalDuration, expandedAreaPrice };
    });
  }, [totalPrice, totalDuration, expandedAreaPrice]);

  // Ref to store cancelDebounce function from DraftAutoSaver
  // This avoids circular dependency since DraftAutoSaver is rendered inside the provider
  const cancelDebounceRef = useRef<(() => void) | null>(null);

  // Expose cancelDebounce that delegates to the ref
  const cancelDebounce = useCallback(() => {
    if (cancelDebounceRef.current) {
      cancelDebounceRef.current();
    }
  }, []);

  /**
   * Keep the discount and the cart in step, in both directions.
   *
   * Dropping a code the cart stopped qualifying for was always here. The return
   * leg is what makes a campaign link work: "book an inspection, get half off
   * radon" arrives matching nothing, so the code waits in
   * `pendingDiscountCode`, and adding the qualifying service — from the AI
   * upsell page, the add-ons list, or a bundle — applies it. Without it the
   * booker did exactly what the ad asked and still paid full price.
   *
   * Living here rather than in the components that add services means every
   * path into the cart is covered by one rule, and none of them has to know
   * discounts exist.
   *
   * The two legs are mutually exclusive and their guards are complements — a
   * code is applied or pending, never both, and each transition lands the next
   * run in the branch that returns early — so this settles in one pass and
   * cannot oscillate.
   *
   * A code the customer cleared by hand is not held, so it stays gone: manual
   * removal leaves both slots empty and neither leg has anything to act on.
   */
  useEffect(() => {
    if (appliedDiscountCode) {
      // An all-services code cannot stop applying while the cart has anything
      // in it, and an empty cart has no price to discount either way.
      if (appliedDiscountCode.appliesToAllServices) return;

      const stillEligibleServiceIds = getEligibleServiceIds(
        appliedDiscountCode,
        selectedServiceIds
      );
      if (stillEligibleServiceIds.length > 0) return;

      setAppliedDiscountCode(null);
      setAppliedDiscountServiceIds([]);
      // Set aside rather than discarded: re-adding the service they just
      // removed has to bring the discount back with it.
      setPendingDiscountCode(appliedDiscountCode);
      // The customer may be nowhere near the discount input when this fires
      // (a link applies it on step 1), so explain the price change instead of
      // letting the total quietly jump back up.
      const notice = discountRemovedNotice(appliedDiscountCode.code);
      setPrefillNotices((prev) =>
        prev.some((existing) => existing.message === notice.message)
          ? prev
          : [...prev, notice]
      );
      return;
    }

    if (!pendingDiscountCode) return;

    const newlyEligibleServiceIds = getEligibleServiceIds(
      pendingDiscountCode,
      selectedServiceIds
    );
    if (newlyEligibleServiceIds.length === 0) return;

    setAppliedDiscountCode(pendingDiscountCode);
    // Mirror how a link-applied code is pinned: an all-services code follows the
    // whole cart, a service-specific one is pinned to what it actually covers.
    setAppliedDiscountServiceIds(
      pendingDiscountCode.appliesToAllServices
        ? selectedServiceIds
        : newlyEligibleServiceIds
    );
    setPendingDiscountCode(null);
    // "We removed your code" is answered by the code coming back, so retract it
    // rather than leaving a stale contradiction above a discounted total.
    setPrefillNotices((prev) =>
      prev.some((notice) => notice.code === 'promo-removed')
        ? prev.filter((notice) => notice.code !== 'promo-removed')
        : prev
    );
  }, [selectedServiceIds, appliedDiscountCode, pendingDiscountCode]);

  // Method to start a new quote (immediately create quote in database with current data)
  const startNewQuote = useCallback(async () => {
    console.log(
      '[SchedulerContext] startNewQuote called - creating new quote immediately with current data'
    );
    console.log('[SchedulerContext] Previous draftQuoteId:', draftQuoteId);

    // Set flag to prevent auto-saves during creation
    setIsCreatingNewQuote(true);
    console.log('[SchedulerContext] isCreatingNewQuote set to true - preventing auto-saves');

    // Cancel any pending debounced saves to prevent race conditions
    cancelDebounce();
    console.log('[SchedulerContext] Cancelled pending debounced saves');

    // Clear draft from localStorage
    clearDraftFromStorage();

    // Clear old draft identifiers BEFORE creating new quote
    // This prevents auto-save from using the old draft ID if it somehow runs
    const oldDraftId = draftQuoteId;
    setDraftQuoteId(null);
    setDraftQuoteSlug(null);
    setShouldResumeDraft(false);
    console.log('[SchedulerContext] Cleared old draft ID:', oldDraftId);

    try {
      // Transform AddressForScheduler to PropertyType for backend
      const propertyData = address
        ? {
            street: address.street,
            unit: address.unit,
            city: address.city,
            state: address.state,
            zip: address.zip,
            lat: address.lat,
            lng: address.lng,
            sqft: address.sqft,
            yearBuilt: address.yearBuilt,
            unitCount: address.unitCount,
            foundation: address.foundation,
            // The online scheduler never captures a Zestimate (capture is
            // internal-only), but a draft hydrated here may already carry one
            // from an internal scheduler — preserved rather than dropped.
            zestimates: address.zestimates?.length
              ? address.zestimates
              : undefined,
          }
        : undefined;

      // Build people array from primary contact + additional contacts
      const peopleArray = [];

      // Add primary contact if available
      if (contact?._id && selectedRole?._id) {
        peopleArray.push({
          _contactId: contact._id,
          _roleId: selectedRole._id,
          primary: true,
        });
      }

      // Add additional contacts
      if (additionalContacts && additionalContacts.length > 0) {
        additionalContacts.forEach((addContact) => {
          if (addContact._contactId?._id && addContact._roleId) {
            peopleArray.push({
              _contactId: addContact._contactId._id,
              _roleId: addContact._roleId,
              primary: addContact.primary || false,
            });
          }
        });
      }

      // Create charge objects from finalServices
      const chargeObjects = finalServices.length > 0 ? createChargeObjects(finalServices) : [];

      // Immediately create a quote in the database with all current data
      // This ensures we capture the lead and all data they've already entered
      // Backend returns Quote directly (not wrapped)
      const response = await callServerAction<Quote>({
        url: 'quote/with-charges',
        method: 'post',
        data: {
          status: 'draft',
          // No currentDraftId - this creates a new quote
          quote: {
            _companyId: companyId,
            property: propertyData,
            requiredInfoValues: requiredInfoValues.length > 0 ? requiredInfoValues : undefined,
            equipmentQuantities: equipmentQuantities.length > 0 ? equipmentQuantities : undefined,
            duration: priceData?.totalDuration,
            total: priceData?.totalPrice,
            selectedPrimaryServiceId: selectedPrimaryService || undefined,
            selectedBundleId: selectedBundle || undefined,
            selectedAddonIds: selectedAddons.length > 0 ? selectedAddons : undefined,
            people: peopleArray.length > 0 ? peopleArray : undefined,
          },
          charges: chargeObjects,
          contactData: {
            email: contact?.email,
            firstName: contact?.firstName,
            lastName: contact?.lastName,
            phone: contact?.phone,
            contactId: contact?._id,
            selectedRoleId: selectedRole?._id,
          },
        },
        params: {
          _companyId: companyId,
        },
        headers: {
          'client-token': jwtToken,
        },
      });

      if (response?._id) {
        // Set the new draft quote ID and slug
        setDraftQuoteId(response._id);
        setDraftQuoteSlug(response.slug);
        console.log(
          '[SchedulerContext] ✅ New quote created immediately with current data:',
          response.slug
        );
        console.log('[SchedulerContext] Quote ID:', response._id);
      } else {
        console.error('[SchedulerContext] Failed to create new quote - no response');
      }
    } catch (error) {
      console.error('[SchedulerContext] Failed to create new quote:', error);
      // Don't throw - let the orchestrator create it on first save if needed
    } finally {
      // Always clear the flag, even if creation failed
      setIsCreatingNewQuote(false);
      console.log('[SchedulerContext] isCreatingNewQuote set to false - auto-saves can resume');
    }

    // Note: We don't clear other form data (contact, services, etc.) here
    // as the user may want to keep some selections from their previous draft.
    // Each field will be overwritten as the user progresses through the flow.
  }, [
    draftQuoteId,
    setDraftQuoteId,
    setDraftQuoteSlug,
    setIsCreatingNewQuote,
    cancelDebounce,
    address,
    contact,
    selectedRole,
    additionalContacts,
    selectedPrimaryService,
    selectedBundle,
    selectedAddons,
    requiredInfoValues,
    equipmentQuantities,
    priceData,
    finalServices,
    companyId,
    jwtToken,
  ]);

  // Manage working hold for online scheduler
  useEffect(() => {
    const updateHold = async () => {
      const expiresInMinutes = 10;
      const { hold } = await manageWorkingHold({
        currentHold: workingHold,
        inspectorId: selectedSlotInspectorInfo?._id || null,
        datetime: selectedDateTime,
        durationHours: priceData?.totalDuration || 0,
        expiresInMinutes,
        jwtToken,
        companyId,
      });

      setWorkingHold(hold);

      // Set expiration time if hold was created
      if (hold) {
        const expiresAt = dayjs().add(expiresInMinutes, 'minute');
        setWorkingHoldExpiresAt(expiresAt);
      } else {
        setWorkingHoldExpiresAt(null);
      }
    };

    updateHold();
  }, [selectedDateTime, selectedSlotInspectorInfo, priceData?.totalDuration]);

  // console.log(additionalContacts);

  return (
    <SchedulerContext.Provider
      value={{
        jwtToken,
        companyId,
        company,
        serviceFilter,
        scheduleSettings,
        inspectors,
        services,
        companyStyles,
        selectedPrimaryService,
        setSelectedPrimaryService,
        selectedBundle,
        setSelectedBundle,
        selectedAddons,
        setSelectedAddons,
        selectedServiceIds,
        selectedRoleIds,
        setSelectedRoleIds,
        selectedRole,
        setSelectedRole,
        contact,
        setContact,
        additionalContacts,
        setAdditionalContacts,
        address,
        setAddress,
        streetViewUrl,
        setStreetViewUrl,
        zillowPropertyData,
        setZillowPropertyData,
        containingServiceAreaArray,
        setContainingServiceAreaArray,
        propertyDetails,
        setPropertyDetails,
        aiRecommendations,
        setAiRecommendations,
        isLoadingAiRecommendations,
        setIsLoadingAiRecommendations,
        selectedDateTime,
        setSelectedDateTime,
        selectedInspectorId,
        setSelectedInspectorId,
        selectedInspectorForSlot,
        setSelectedInspectorForSlot,
        selectedInspectorIdsForFilter,
        setSelectedInspectorIdsForFilter,
        selectedSlotInspectorInfo,
        setSelectedSlotInspectorInfo,
        allRequiredInfo,
        requiredInfoValues,
        setRequiredInfoValues,
        equipmentQuantities,
        setEquipmentQuantities,
        referralSource,
        setReferralSource,
        closingDate,
        setClosingDate,
        // Pricing & Calculated Services
        finalServices,
        setFinalServices,
        priceData,
        // Bundle and Discount Data
        bundles,
        discountCodes,
        appliedDiscountCode,
        appliedDiscountServiceIds,
        setAppliedDiscountCode,
        setAppliedDiscountServiceIds,
        pendingDiscountCode,
        setPendingDiscountCode,
        pendingDiscountUnlockServices,
        // Lead Capture - Draft Quote
        draftQuoteId,
        setDraftQuoteId,
        draftQuoteSlug,
        setDraftQuoteSlug,
        wcLeadId,
        setWcLeadId,
        isLoadingDraft,
        setIsLoadingDraft,
        shouldResumeDraft,
        setShouldResumeDraft,
        isCheckingDrafts,
        setIsCheckingDrafts,
        isCreatingNewQuote,
        setIsCreatingNewQuote,
        startNewQuote,
        // Working Hold
        workingHold,
        setWorkingHold,
        workingHoldExpiresAt,
        setWorkingHoldExpiresAt,
        // Booking State
        bookingStatus,
        setBookingStatus,
        // Sending Quote State
        isSendingQuote,
        setIsSendingQuote,
        // Escape Hatch State
        escapeHatchReason,
        setEscapeHatchReason,
        // Property step progress
        seenPropertyPages,
        markPropertyPageSeen,
        hasSeenPropertyPages,
        resetPropertyStepProgress,
        // Scheduler Notes
        schedulerNotes,
        setSchedulerNotes,
        // Draft save orchestrator
        cancelDebounce,
        // Internal ref setter for DraftAutoSaver to use
        setCancelDebounceRef: (fn: (() => void) | null) => {
          cancelDebounceRef.current = fn;
        },
        contactRoles,
        // WhatConverts tracking
        wcProfileId,
        // Scheduling-link pre-fill
        prefill,
        prefillNotices,
        dismissPrefillNotices,
      }}
    >
      {children}
    </SchedulerContext.Provider>
  );
};

export const useScheduler = () => {
  const context = useContext(SchedulerContext);
  if (!context) {
    throw new Error('useScheduler must be used within SchedulerProvider');
  }
  return context;
};
